import { store } from '../store';
import { config } from '../config';

const API_URL = config.API_URL;

interface AuditFilters {
    user_id?: string;
    event_type?: string;
    date_from?: string;
    date_to?: string;
    risk_level?: string;
    search?: string;
}

interface AuditPagination {
    page?: number;
    limit?: number;
}

interface AuditLog {
    id: number;
    event_type: string;
    user_id?: string;
    target_user_id?: string;
    resource_type?: string;
    resource_id?: string;
    action: string;
    details?: any;
    ip_address?: string;
    user_agent?: string;
    success: boolean;
    risk_level: string;
    created_at: string;
    workspace_id?: string;
    user_email?: string;
    target_user_email?: string;
    workspace_name?: string;
    display_email?: string;
}

interface AuditStatistics {
    total_events: number;
    today_events: number;
    week_events: number;
    month_events: number;
    high_risk_events: number;
    medium_risk_events: number;
    low_risk_events: number;
    failed_logins?: number;
}

interface AuditLogResponse {
    logs: AuditLog[];
    pagination: {
        current_page: number;
        total_pages: number;
        total_records: number;
        per_page: number;
        has_next: boolean;
        has_prev: boolean;
    };
    statistics: AuditStatistics;
    applied_filters: AuditFilters;
}

interface AuditStatsResponse {
    statistics: AuditStatistics;
    event_types: Array<{event_type: string; count: number}>;
    recent_high_risk: AuditLog[];
}

export class AuditService {
    private static authToken: string | null = null;

    private static async request<T>(
        endpoint: string,
        method: string = 'GET',
        body?: any
    ): Promise<T> {
        try {
            const response = await fetch(`${API_URL}/audit${endpoint}`, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                },
                body: body ? JSON.stringify(body) : undefined,
                credentials: 'include', // Send cookies for session auth
            });

            // Check if response is ok before trying to parse JSON
            if (!response.ok) {
                let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.message || errorMessage;
                    console.error('API Error:', errorData);
                } catch (parseError) {
                    console.error('Failed to parse error response:', parseError);
                }
                throw new Error(errorMessage);
            }

            // Parse JSON response
            try {
                const data = await response.json();
                return data;
            } catch (parseError) {
                console.error('Failed to parse successful response:', parseError);
                throw new Error('Invalid response format from server');
            }

        } catch (error) {
            if (error instanceof TypeError && error.message.includes('fetch')) {
                console.error('Network error:', error);
                throw new Error('Network connection failed. Please check your internet connection.');
            }
            console.error('API request failed:', error);
            throw error;
        }
    }

    /**
     * Get audit logs with filtering and pagination
     */
    static async getLogs(filters: AuditFilters = {}, pagination: AuditPagination = {}): Promise<AuditLogResponse> {
        try {
            const params = new URLSearchParams();
            
            // Add filters
            if (filters.user_id) params.append('user_id', filters.user_id);
            if (filters.event_type) params.append('event_type', filters.event_type);
            if (filters.date_from) params.append('date_from', filters.date_from);
            if (filters.date_to) params.append('date_to', filters.date_to);
            if (filters.risk_level) params.append('risk_level', filters.risk_level);
            if (filters.search) params.append('search', filters.search);
            
            // Add pagination
            if (pagination.page) params.append('page', pagination.page.toString());
            if (pagination.limit) params.append('limit', pagination.limit.toString());

            return await this.request<AuditLogResponse>(`/logs?${params}`);
        } catch (error) {
            console.error('Failed to fetch audit logs:', error);
            throw error;
        }
    }

    /**
     * Get audit statistics
     */
    static async getStats(): Promise<AuditStatsResponse> {
        try {
            return await this.request<AuditStatsResponse>('/stats');
        } catch (error) {
            console.error('Failed to fetch audit statistics:', error);
            throw error;
        }
    }

    /**
     * Get security alerts
     */
    static async getSecurityAlerts(limit = 10) {
        try {
            return await this.request(`/alerts?limit=${limit}`);
        } catch (error) {
            console.error('Failed to fetch security alerts:', error);
            throw error;
        }
    }

    /**
     * Get activity summary
     */
    static async getActivitySummary(days = 7) {
        try {
            return await this.request(`/summary?days=${days}`);
        } catch (error) {
            console.error('Failed to fetch activity summary:', error);
            throw error;
        }
    }

    /**
     * Get audit logs for specific user
     */
    static async getUserLogs(userId: string, page = 1, limit = 20) {
        try {
            return await this.request(`/user/${userId}?page=${page}&limit=${limit}`);
        } catch (error) {
            console.error('Failed to fetch user audit logs:', error);
            throw error;
        }
    }

    /**
     * Export audit logs to CSV
     */
    static async exportLogs(filters: AuditFilters = {}) {
        try {
            const params = new URLSearchParams();
            
            if (filters.event_type) params.append('event_type', filters.event_type);
            if (filters.risk_level) params.append('risk_level', filters.risk_level);
            if (filters.date_from) params.append('date_from', filters.date_from);
            if (filters.date_to) params.append('date_to', filters.date_to);

            const response = await fetch(`${API_URL}/audit/export?${params}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include', // Send cookies for session auth
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to export audit logs');
            }

            const blob = await response.blob();
            const filename = response.headers.get('content-disposition')?.match(/filename="(.+)"/)?.[1] 
                || `audit_logs_${new Date().toISOString().split('T')[0]}.csv`;
            
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            return { success: true };
        } catch (error) {
            console.error('Error exporting audit logs:', error);
            throw error;
        }
    }

    /**
     * Format event details for display
     */
    static formatEventDetails(details: any): string {
        if (!details) return 'No details';
        
        if (typeof details === 'string') {
            try {
                details = JSON.parse(details);
            } catch {
                return details;
            }
        }

        const formatted = [];
        
        for (const [key, value] of Object.entries(details)) {
            const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            formatted.push(`${formattedKey}: ${value}`);
        }
        
        return formatted.join(', ');
    }

    /**
     * Get risk level color for UI
     */
    static getRiskLevelColor(riskLevel: string): string {
        const colors: { [key: string]: string } = {
            low: 'text-green-600 bg-green-100',
            medium: 'text-yellow-600 bg-yellow-100',
            high: 'text-orange-600 bg-orange-100',
            critical: 'text-red-600 bg-red-100'
        };
        return colors[riskLevel] || colors.low;
    }

    /**
     * Get event type icon
     */
    static getEventTypeIcon(eventType: string): string {
        const icons: { [key: string]: string } = {
            authentication: '🔐',
            data_access: '📊',
            administration: '👤',
            test: '🧪'
        };
        return icons[eventType] || '📋';
    }
}

export default AuditService;
